import { FC } from 'react';
import { AsyncImage } from '../../AsyncImage/AsyncImage';
import PublisherLogo from '../../PublisherLogo/PublisherLogo';
import { StorySize } from '@/moment/types/story-types';
import { EnhancedLightboxItem } from '@moment/net/softbox-types.ts';
import './CarouselCard.scss';

export interface CarouselCardProps {
  type: 'article' | 'product';
  imageSrc: string;
  imageAlt: string;
  title: string;
  publisherLogo?: string;
  darkPublisherLogo?: string;
  publisherName?: string;
  onClick?: () => void;
  // Product specific props
  price?: string;
  currency?: string;
  // Theme override
  isDark?: boolean;
  // Tracking props for impression events
  contentId?: string;
  contentOwner?: string;
  primaryInterest?: string;
  // Original item for impression tracking
  originalItem?: EnhancedLightboxItem;
}

const CarouselCard: FC<CarouselCardProps> = ({
  type,
  imageSrc,
  imageAlt,
  title,
  publisherLogo,
  darkPublisherLogo,
  publisherName,
  onClick,
  price,
  currency,
  isDark,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const renderArticleCard = () => {
    return (
      <div
        className="carousel-card carousel-card--article"
        onClick={handleClick}
      >
        <div className="carousel-card__image-container">
          <AsyncImage
            src={imageSrc}
            alt={imageAlt}
            containerClassName="carousel-card__image"
          />
        </div>
        <div className="carousel-card__content">
          {publisherLogo && (
            <div className="carousel-card__publisher">
              <PublisherLogo
                publisherLogo={publisherLogo}
                publisherName={publisherName || ''}
                darkPublisherLogo={darkPublisherLogo}
                storySize={StorySize.CAROUSEL}
                isDark={isDark}
              />
            </div>
          )}
          <h3 className="carousel-card__title title-sm">{title}</h3>
        </div>
      </div>
    );
  };

  const renderProductCard = () => {
    return (
      <div
        className="carousel-card carousel-card--product"
        onClick={handleClick}
      >
        <div className="carousel-card__image-container">
          <AsyncImage
            src={imageSrc}
            alt={imageAlt}
            containerClassName="carousel-card__image"
          />
        </div>
        <div className="carousel-card__content">
          {price && (
            <div className="carousel-card__price title-md">
              {currency && (
                <span className="carousel-card__currency">{currency}</span>
              )}
              <span className="carousel-card__price-amount">{price}</span>
            </div>
          )}
          <h3 className="carousel-card__title">{title}</h3>
        </div>
      </div>
    );
  };

  return type === 'product' ? renderProductCard() : renderArticleCard();
};

export default CarouselCard;
